/**
 * SpokedSpinner
 *
 * A self-contained 12-bar spoked radial loader (the classic "fade12" spinner):
 * twelve tapered grey bars arranged radially, each rotated 30deg more than the
 * previous, with a staggered animationDelay so opacity "chases" around the
 * circle. Self-contained — keyframes are defined inline via MUI styled(), no
 * external CSS — matching the keyframes pattern used in `LayoutBoxes.tsx`
 * (`PulseDot` / `PulseDotSmall`).
 *
 * Used as a drop-in replacement for MUI's `<CircularProgress />` in the
 * "Generating SOAP Note" panel.
 *
 * @param size - Spinner diameter in px (default 48). Bars scale relative to it.
 * @example <SpokedSpinner /> // 48px grey spoked spinner
 * @example <SpokedSpinner size={32} /> // smaller, same proportions
 */

import { Box, styled } from '@mui/material';

const BAR_COUNT = 12;
const PERIOD_SECONDS = 1.2;
const BAR_COLOR = '#9aa0a6';

/**
 * Root box. Sized `size`x`size`; holds the 12 absolutely-positioned bars,
 * each centered then rotated/translated outward to form the radial spokes.
 */
const SpinnerRoot = styled(Box)<{ size?: number }>(({ size = 48 }) => ({
  position: 'relative',
  width: size,
  height: size,
  '& .spoked-spinner-bar': {
    position: 'absolute',
    top: '0%',
    left: '50%',
    width: Math.max(2, Math.round(size * 0.042)),
    height: `${Math.round(size * 0.25)}px`,
    marginLeft: -Math.max(1, Math.round(size * 0.021)),
    borderRadius: Math.max(2, Math.round(size * 0.042)),
    backgroundColor: BAR_COLOR,
    transformOrigin: `center ${size / 2}px`,
    animationName: 'spokedFade12',
    animationDuration: `${PERIOD_SECONDS}s`,
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  '@keyframes spokedFade12': {
    '0%': { opacity: 1 },
    '100%': { opacity: 0.15 },
  },
}));

export function SpokedSpinner({ size = 48 }: { size?: number }) {
  return (
    <SpinnerRoot
      size={size}
      data-testid='generating-spinner'
      role='progressbar'
      aria-label='Generating'
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <Box
          key={i}
          className='spoked-spinner-bar'
          sx={{
            transform: `rotate(${i * (360 / BAR_COUNT)}deg)`,
            animationDelay: `${-PERIOD_SECONDS + i * (PERIOD_SECONDS / BAR_COUNT)}s`,
          }}
        />
      ))}
    </SpinnerRoot>
  );
}
